import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Nav from '../components/Nav';
import { saveResult } from '../api/results';

const scenarios = [
  {
    id: 1, title: 'The Vendor Breach', severity: 'Critical', severityColor: '#ef4444',
    domain: 'Vendor Risk Management',
    situation: 'At 11:47 PM on a Friday, your CISO receives an alert. A third-party analytics vendor your engineering team integrated 18 months ago has suffered a data breach. They processed behavioral data on 2.3 million of your users. The vendor has not yet issued a public statement. Your legal team is unreachable.',
    inject: 'Your CTO asks: What data did we share with this vendor? What are our obligations under PIPEDA and GDPR? Do we notify users — and by when?',
    choices: [
      { text: 'Immediately notify all affected users and regulators within 24 hours', score: 60, feedback: 'Proactive — but without knowing the full scope, premature notification can cause panic and legal exposure. Timing matters.' },
      { text: 'Convene your privacy incident response team and assess data sharing agreements first', score: 100, feedback: 'Correct. Assessing what data was shared, under what agreement, and the regulatory timelines (72 hrs GDPR, expedited PIPEDA) before acting is the right first move.' },
      { text: 'Wait for the vendor to issue a public statement before taking action', score: 0, feedback: 'High risk. Regulators expect you to act on known risk, not wait for vendors. Under GDPR you have 72 hours from awareness — not from public disclosure.' },
      { text: 'Engage external legal counsel and issue a holding statement internally', score: 80, feedback: 'Good instinct. External counsel protects privilege. However, the privacy response team should be running parallel to legal — not waiting on it.' },
    ],
    gap: 'Third-party data processing agreements and incident response playbooks'
  },
  {
    id: 2, title: 'The AI Training Trap', severity: 'High', severityColor: '#f97316',
    domain: 'AI Governance',
    situation: 'Your product team has been using customer support transcripts to fine-tune an internal AI model for six months. The data includes names, email addresses, and detailed descriptions of customer complaints. No one flagged this to the privacy team. The model is now being considered for a customer-facing feature.',
    inject: 'Your Chief Product Officer presents the model in a board meeting as a competitive advantage. A board member asks: "Did customers consent to their data being used to train AI?"',
    choices: [
      { text: 'Confirm consent was implied in the terms of service and proceed', score: 20, feedback: 'Risky. Implied consent for AI training is increasingly challenged by regulators. GDPR requires explicit, specific consent for automated processing.' },
      { text: 'Pause the product launch and conduct a Data Protection Impact Assessment (DPIA)', score: 100, feedback: 'Correct. A DPIA is required under GDPR for high-risk processing including AI and automated decision-making. Pausing is the right call.' },
      { text: 'Retroactively inform customers via an email update and continue the launch', score: 40, feedback: 'Informing is not the same as consenting. Retroactive notice does not legitimize prior processing. The DPIA and legal basis review must happen first.' },
      { text: 'Delete the model and all training data immediately', score: 30, feedback: 'While protective, this destroys potential evidence needed for regulatory response. A measured legal review is needed before deletion.' },
    ],
    gap: 'AI training data governance, DPIA process, and consent frameworks for automated processing'
  },
  {
    id: 3, title: 'The Insider Access Incident', severity: 'High', severityColor: '#f97316',
    domain: 'Access Control & Incident Response',
    situation: 'A routine audit reveals that a recently departed senior engineer had access to your production database — including PII for 800,000 users — for 47 days after their termination date. There is no evidence of data exfiltration, but access logs are incomplete due to a logging configuration error.',
    inject: 'Your Head of Security presents this to leadership. The COO asks: "Is this a reportable breach? What\'s our exposure?"',
    choices: [
      { text: 'Since there\'s no evidence of exfiltration, treat it as an access control failure — not a breach', score: 50, feedback: 'Partially correct. The absence of evidence is not evidence of absence. Regulators may still consider this reportable. A risk assessment is required.' },
      { text: 'Conduct a forensic investigation, assess reportability, and fix the logging gap immediately', score: 100, feedback: 'Correct. You must determine reportability through a structured risk assessment. The logging gap is a secondary but critical finding. Both must run in parallel.' },
      { text: 'Report immediately to regulators as a precaution', score: 60, feedback: 'Well-intentioned but premature. Reporting without completing a risk assessment can trigger unnecessary regulatory scrutiny.' },
      { text: 'Remediate access controls and close the finding internally with no further action', score: 10, feedback: 'Highest risk answer. Failing to assess and potentially report a known access control failure is a serious compliance gap.' },
    ],
    gap: 'Offboarding access revocation, audit logging completeness, and breach assessment procedures'
  }
];

function getRisk(pct: number) {
  if (pct >= 80) return { label: 'Low Risk', color: '#22c55e' };
  if (pct >= 50) return { label: 'Moderate Risk', color: '#f97316' };
  return { label: 'High Risk', color: '#ef4444' };
}

export default function Tabletop() {
  const navigate = useNavigate();
  const [screen, setScreen] = useState<'intro' | 'exercise' | 'results'>('intro');
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [scores, setScores] = useState<number[]>([]);
  const [answers, setAnswers] = useState<number[]>([]);

  const scenario = scenarios[current];
  const totalScore = scores.reduce((a, b) => a + b, 0);
  const pct = Math.round((totalScore / (scenarios.length * 100)) * 100);
  const risk = getRisk(pct);

  async function handleReveal() {
    if (selected === null) return;
    const score = scenario.choices[selected].score;
    const newScores = [...scores, score];
    const newAnswers = [...answers, selected];
    setScores(newScores);
    setAnswers(newAnswers);
    setRevealed(true);

    // Save module result
    await saveResult({
      assessmentType: 'tabletop',
      moduleId: `scenario-${scenario.id}`,
      score,
      answers: newAnswers,
      isComplete: false
    });
  }

  async function handleNext() {
    if (current < scenarios.length - 1) {
      setCurrent(c => c + 1);
      setSelected(null);
      setRevealed(false);
    } else {
      // Save final complete result
      const finalScore = Math.round((scores.reduce((a, b) => a + b, 0) / (scenarios.length * 100)) * 100);
      await saveResult({
        assessmentType: 'tabletop',
        moduleId: 'complete',
        score: finalScore,
        answers,
        isComplete: true
      });
      setScreen('results');
    }
  }

  const base: React.CSSProperties = { minHeight: '100vh', background: 'linear-gradient(135deg,#0a0a1a,#0d1b3e,#050d1f)', fontFamily: "'Segoe UI',system-ui,sans-serif", color: '#fff', paddingTop: 80 };
  const wrap: React.CSSProperties = { maxWidth: 760, margin: '0 auto', padding: '32px 16px' };

  return (
    <div style={base}>
      <Nav />

      {screen === 'intro' && (
        <div style={{ ...wrap, textAlign: 'center' }}>
          <div style={{ display: 'inline-block', background: 'rgba(168,85,247,0.15)', border: '1px solid rgba(168,85,247,0.3)', padding: '5px 14px', borderRadius: 100, fontSize: 11, color: '#c084fc', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 24 }}>CXO · Privacy Tabletop</div>
          <h1 style={{ fontSize: 'clamp(24px,5vw,40px)', fontWeight: 700, letterSpacing: -1, marginBottom: 16 }}>How does your organization respond<br /><span style={{ color: '#c084fc' }}>when privacy fails?</span></h1>
          <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.55)', lineHeight: 1.8, maxWidth: 520, margin: '0 auto 36px' }}>Three real-world privacy incidents. Your decisions are scored and mapped to a Privacy Risk Report.</p>
          <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 40 }}>
            {[['3', 'Scenarios'], ['15', 'Minutes'], ['CXO', 'Level']].map(([v, l]) => (
              <div key={l} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14, padding: '16px 24px', textAlign: 'center' }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: '#c084fc' }}>{v}</div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>{l}</div>
              </div>
            ))}
          </div>
          <button onClick={() => setScreen('exercise')} style={{ background: 'linear-gradient(135deg,#a855f7,#7c3aed)', border: 'none', color: '#fff', padding: '15px 40px', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: 'pointer', width: '100%', maxWidth: 340 }}>Begin Assessment →</button>
        </div>
      )}

      {screen === 'exercise' && (
        <div style={wrap}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28 }}>
            {scenarios.map((_, i) => (
              <div key={i} style={{ flex: 1, height: 4, borderRadius: 4, background: i < current ? '#a855f7' : i === current ? 'linear-gradient(90deg,#a855f7,#6366f1)' : 'rgba(255,255,255,0.08)' }} />
            ))}
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', whiteSpace: 'nowrap' }}>{current + 1} / {scenarios.length}</span>
          </div>

          <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 18, padding: 24, marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
              <div style={{ background: `${scenario.severityColor}20`, border: `1px solid ${scenario.severityColor}50`, color: scenario.severityColor, fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '3px 10px', borderRadius: 100 }}>{scenario.severity}</div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: 1, padding: '3px 0' }}>{scenario.domain}</div>
            </div>
            <h2 style={{ fontSize: 'clamp(18px,3vw,24px)', fontWeight: 700, marginBottom: 14 }}>{scenario.title}</h2>
            <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.65)', lineHeight: 1.8, marginBottom: 18 }}>{scenario.situation}</p>
            <div style={{ background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.25)', borderRadius: 12, padding: '14px 18px' }}>
              <div style={{ fontSize: 10, color: '#a5b4fc', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6, fontWeight: 700 }}>Inject</div>
              <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.75)', lineHeight: 1.7 }}>{scenario.inject}</p>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
            {scenario.choices.map((choice, idx) => {
              const isSel = selected === idx;
              const isCorrect = revealed && choice.score === 100;
              const isWrong = revealed && isSel && choice.score !== 100;
              return (
                <button key={idx} onClick={() => !revealed && setSelected(idx)} style={{
                  background: isCorrect ? 'rgba(34,197,94,0.1)' : isWrong ? 'rgba(239,68,68,0.08)' : isSel ? 'rgba(168,85,247,0.15)' : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${isCorrect ? 'rgba(34,197,94,0.4)' : isWrong ? 'rgba(239,68,68,0.3)' : isSel ? 'rgba(168,85,247,0.45)' : 'rgba(255,255,255,0.09)'}`,
                  borderRadius: 12, padding: '14px 16px', textAlign: 'left', cursor: revealed ? 'default' : 'pointer', width: '100%'
                }}>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                    <div style={{ width: 22, height: 22, borderRadius: '50%', border: `1.5px solid ${isCorrect ? '#22c55e' : isWrong ? '#ef4444' : isSel ? '#a855f7' : 'rgba(255,255,255,0.2)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: isCorrect ? '#22c55e' : isWrong ? '#ef4444' : isSel ? '#a855f7' : 'transparent', flexShrink: 0, marginTop: 2 }}>
                      {isCorrect ? '✓' : isWrong ? '✗' : isSel ? '●' : ''}
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.85)', margin: 0, lineHeight: 1.6 }}>{choice.text}</p>
                      {revealed && (isSel || isCorrect) && (
                        <p style={{ fontSize: 13, color: isCorrect ? '#86efac' : '#fca5a5', marginTop: 10, lineHeight: 1.65 }}>{choice.feedback}</p>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {!revealed
            ? <button onClick={handleReveal} disabled={selected === null} style={{ background: selected !== null ? 'linear-gradient(135deg,#a855f7,#7c3aed)' : 'rgba(255,255,255,0.06)', border: 'none', color: selected !== null ? '#fff' : 'rgba(255,255,255,0.25)', padding: 14, borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: selected !== null ? 'pointer' : 'default', width: '100%' }}>Submit Response</button>
            : <button onClick={handleNext} style={{ background: 'linear-gradient(135deg,#a855f7,#7c3aed)', border: 'none', color: '#fff', padding: 14, borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: 'pointer', width: '100%' }}>{current < scenarios.length - 1 ? 'Next Scenario →' : 'View Risk Report →'}</button>
          }
        </div>
      )}

      {screen === 'results' && (
        <div style={wrap}>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 2, color: '#c084fc', marginBottom: 10 }}>Assessment Complete</div>
            <h2 style={{ fontSize: 'clamp(22px,4vw,34px)', fontWeight: 700, letterSpacing: -1, marginBottom: 8 }}>Your Privacy Risk Report</h2>
            <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.4)' }}>Based on your responses across 3 incident scenarios</p>
          </div>

          <div style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${risk.color}40`, borderRadius: 20, padding: 32, textAlign: 'center', marginBottom: 20 }}>
            <div style={{ fontSize: 80, fontWeight: 700, color: risk.color, lineHeight: 1 }}>{pct}</div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', marginTop: 4, marginBottom: 16 }}>Privacy Readiness Score</div>
            <div style={{ display: 'inline-block', background: `${risk.color}20`, border: `1px solid ${risk.color}50`, color: risk.color, padding: '5px 18px', borderRadius: 100, fontSize: 13, fontWeight: 700 }}>{risk.label}</div>
            <div style={{ height: 8, background: 'rgba(255,255,255,0.07)', borderRadius: 8, marginTop: 24, overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', background: risk.color, borderRadius: 8 }} />
            </div>
          </div>

          <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)', borderRadius: 20, padding: 24, marginBottom: 20 }}>
            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 2, color: '#c084fc', marginBottom: 16, fontWeight: 700 }}>Identified Gaps</div>
            {scenarios.map((s, i) => {
              const r = getRisk(scores[i] || 0);
              return (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, padding: '14px 0', borderBottom: i < scenarios.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', marginBottom: 4 }}>{s.domain}</div>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', lineHeight: 1.5 }}>{s.gap}</div>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: r.color, flexShrink: 0 }}>{r.label}</div>
                </div>
              );
            })}
          </div>

          <a href="mailto:omer@privacy.education?subject=Tabletop%20Debrief%20Request" style={{ display: 'block', background: 'linear-gradient(135deg,#a855f7,#7c3aed)', color: '#fff', padding: 16, borderRadius: 12, fontSize: 15, fontWeight: 700, textAlign: 'center', textDecoration: 'none', marginBottom: 12 }}>Book a Debrief →</a>
          <button onClick={() => navigate('/')} style={{ display: 'block', width: '100%', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)', padding: 14, borderRadius: 12, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Back to Dashboard</button>
        </div>
      )}
    </div>
  );
}